import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { UserPlusIcon } from "@heroicons/react/24/outline";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";

import { useToggle } from "@hooks/hooks";
import { classNames } from "@utils";
import { toast } from "@components/hot-toast";
import Toast from "@components/notifications/Toast";

interface OnboardFormFields {
  email: string;
  password: string;
  confirmPassword: string;
  displayName: string;
}

export const Onboarding = () => {
  const navigate = useNavigate();

  // Check if onboarding is needed
  const { data: needsOnboarding, isLoading } = useQuery({
    queryKey: ["auth-check"],
    queryFn: async () => {
      const response = await fetch("/api/v1/auth/check");
      const data = await response.json();
      return data.needs_onboarding;
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: OnboardFormFields) => {
      const response = await fetch("/api/v1/auth/onboard", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: data.email,
          password: data.password,
          display_name: data.displayName || undefined,
        }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to create account");
      }
      return response.json();
    },
    onSuccess: () => {
      toast.custom((t) => (
        <Toast type="success" body="Account created! Please sign in." t={t} />
      ));
      navigate({ to: "/login" });
    },
    onError: (error: Error) => {
      toast.custom((t) => (
        <Toast type="error" body={error.message} t={t} />
      ));
    },
  });

  const form = useForm({
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      displayName: "",
    },
    onSubmit: ({ value }) => {
      if (value.password !== value.confirmPassword) {
        toast.custom((t) => (
          <Toast type="error" body="Passwords don't match" t={t} />
        ));
        return;
      }
      mutation.mutate(value);
    },
  });

  // Redirect if onboarding not needed
  if (!isLoading && !needsOnboarding) {
    navigate({ to: "/login" });
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-200">
          newsbot
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
          Create your admin account to get started
        </p>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div className="px-6 py-12 bg-white dark:bg-gray-800 shadow-sm sm:rounded-lg sm:px-12 border border-gray-150 dark:border-gray-775">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
            className="space-y-6"
          >
            <form.Field
              name="email"
              validators={{
                onChange: ({ value }) => (!value ? "Email is required" : undefined),
              }}
            >
              {({ state, handleChange, handleBlur }) => (
                <div>
                  <label
                    htmlFor="email"
                    className="block ml-px text-xs font-bold text-gray-800 dark:text-gray-100 uppercase tracking-wide"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                    value={state.value}
                    className={classNames(
                      "block mt-1 w-full shadow-xs sm:text-sm rounded-md py-2.5 bg-gray-100 dark:bg-gray-850 dark:text-gray-100",
                      !state.meta.isValid
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300 dark:border-gray-700 focus:ring-blue-500 dark:focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-500"
                    )}
                  />
                  {state.meta.errors && (
                    <p className="mt-1 text-sm text-red-600">{state.meta.errors[0]}</p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="displayName">
              {({ state, handleChange, handleBlur }) => (
                <div>
                  <label
                    htmlFor="displayName"
                    className="block ml-px text-xs font-bold text-gray-800 dark:text-gray-100 uppercase tracking-wide"
                  >
                    Display Name (optional)
                  </label>
                  <input
                    type="text"
                    id="displayName"
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={handleBlur}
                    value={state.value}
                    className="block mt-1 w-full shadow-xs sm:text-sm rounded-md py-2.5 bg-gray-100 dark:bg-gray-850 dark:text-gray-100 border-gray-300 dark:border-gray-700 focus:ring-blue-500 dark:focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-500"
                  />
                </div>
              )}
            </form.Field>

            <form.Field
              name="password"
              validators={{
                onChange: ({ value }) => {
                  if (!value) return "Password is required";
                  if (value.length < 8) return "Password must be at least 8 characters";
                  return undefined;
                },
              }}
            >
              {({ state, handleChange, handleBlur }) => (
                <PasswordField
                  label="Password"
                  id="password"
                  value={state.value}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isValid={state.meta.isValid}
                  error={state.meta.errors?.[0]}
                />
              )}
            </form.Field>

            <form.Field
              name="confirmPassword"
              validators={{
                onChange: ({ value }) => (!value ? "Please confirm password" : undefined),
              }}
            >
              {({ state, handleChange, handleBlur }) => (
                <PasswordField
                  label="Confirm Password"
                  id="confirmPassword"
                  value={state.value}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isValid={state.meta.isValid}
                  error={state.meta.errors?.[0]}
                />
              )}
            </form.Field>

            <button
              type="submit"
              disabled={mutation.isPending}
              className="w-full flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-xs text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              <UserPlusIcon className="w-4 h-4 mr-1.5" />
              {mutation.isPending ? "Creating..." : "Create account"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

interface PasswordFieldProps {
  label: string;
  id: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  isValid: boolean;
  error?: string;
}

function PasswordField({ label, id, value, onChange, onBlur, isValid, error }: PasswordFieldProps) {
  const [isVisible, toggleVisibility] = useToggle(false);

  return (
    <div>
      <label
        htmlFor={id}
        className="block ml-px text-xs font-bold text-gray-800 dark:text-gray-100 uppercase tracking-wide"
      >
        {label}
      </label>
      <div className="relative mt-1">
        <input
          type={isVisible ? "text" : "password"}
          id={id}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          value={value}
          className={classNames(
            "block w-full shadow-xs sm:text-sm rounded-md py-2.5 bg-gray-100 dark:bg-gray-850 dark:text-gray-100 pr-10",
            !isValid
              ? "border-red-500 focus:ring-red-500 focus:border-red-500"
              : "border-gray-300 dark:border-gray-700 focus:ring-blue-500 dark:focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-500"
          )}
        />
        <button
          type="button"
          onClick={toggleVisibility}
          aria-label={isVisible ? "Hide password" : "Show password"}
          className="absolute inset-y-0 right-0 px-3 flex items-center"
        >
          {!isVisible ? (
            <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
          ) : (
            <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-500" />
          )}
        </button>
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
